import React, { useEffect } from 'react';

import { Theme } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import _sortBy from 'lodash/sortBy';
import { hashHistory } from 'react-router';

import { BackLink, Typography } from '../../components/atoms';
import { LoadingSpinner, Page } from '../../components/layouts';
import { NavList } from '../../components/molecules';
import {
  NavListItemProps,
  NavListVariantType,
} from '../../components/molecules/NavListItem/NavListItem';
import { Reorder } from '../../components/organisms/Reorder';
import { PageHeader } from '../../components/partials/PageHeader';
import { analyticsService } from '../../core/services/analyticsService';
import { flagService } from '../../core/services/flagService';
import { useOrderHistory } from '../../hooks/useOrderHistory';
import { useReorderList } from '../../hooks/useReorderList';
import { domUtils, getUtils, storeListUtils } from '../../utils';
import * as Api from '../../weborder/Api';
import {
  DispatchProps,
  StateProps,
} from '../../weborder/component/DeliveryList';
import { RouteParams } from '../../weborder/Routes';

type Props = DispatchProps & StateProps & RouteParams;
const useStyles = makeStyles((theme: Theme) => ({
  disabled: {
    '& .fd-text': {
      color: theme.palette.grey[600],
    },
  },
  preOrder: {
    color: theme.palette.warning.main,
  },

  reorder: {
    display: 'flex',
    marginBottom: theme.spacing(2),
    width: '100%',
  },

  restaurantDetails: {
    color: theme.palette.grey[800],
  },
  restaurantTitle: {
    alignItems: 'center',
    display: 'flex',
    marginBottom: theme.spacing(0.25),

    '& .fd-icon': {
      marginRight: theme.spacing(1),
    },
  },
}));

export function StoreDeliveryList(props: Props): JSX.Element | null {
  const {
    actions,
    deliveryOption,
    isLoggedIn,
    languages,
    params,
    previousOrders,
    translate,
  } = props;
  const { isRestaurantClosedWithNoPreOrders, renderAvailabilityStatusIcon } =
    storeListUtils;
  const classes = useStyles();

  const isReorderEnabled = flagService.isFlagOn('reorder') && isLoggedIn;

  const {
    data: orderHistoryData,
    isEmpty: isOrderHistoryEmpty,
    isError: isOrderHistoryError,
    isLoading: isOrderHistoryLoading,
    isSuccess: isOrderHistorySuccess,
  } = useOrderHistory(
    isReorderEnabled,
    actions.getOrderHistory,
    previousOrders,
  );

  const { reorderList, showReorder } = useReorderList(
    Api.DeliveryType.Delivery,
    isOrderHistoryEmpty,
    isOrderHistoryError,
    isOrderHistoryLoading,
    isOrderHistorySuccess,
    orderHistoryData,
  );

  const languageCode = getUtils.activeLanguageCode(languages);

  useEffect(() => {
    domUtils.skipToContent();
  }, []);

  if (!deliveryOption || deliveryOption.status !== 'success') {
    return <LoadingSpinner />;
  }

  const trackBackSelected = (): void => {
    const eventData = {
      amp_label: 'Back Selected',
      amp_view: 'Store Delivery List',
    };
    analyticsService.track(eventData);
  };

  if (deliveryOption.data.type === 'skip') {
    hashHistory.replace(
      `/restaurant/${deliveryOption.data.to.VirtualRestaurantId}/delivery/${params.location}`,
    );
    return null;
  }

  const renderRestaurantName = (
    acceptPreOrders: boolean,
    isOpen: boolean,
    restaurantName: string,
  ): JSX.Element => (
    <div className={classes.restaurantTitle}>
      {renderAvailabilityStatusIcon(acceptPreOrders, isOpen)}

      <Typography>
        <em>{restaurantName}</em>
      </Typography>
    </div>
  );

  const renderAddress = (address: string) => {
    if (!address) {
      return null;
    }

    return <Typography variant="body2-secondary">{address}</Typography>;
  };

  const renderClosedNotice = (
    acceptPreOrders: boolean,
    isOpen: boolean,
    openTimeMessage: string,
  ): JSX.Element => {
    let preOrder = '';

    if (isOpen) {
      return null;
    }

    if (acceptPreOrders) {
      preOrder = translate('StoreCollectionList_PreorderNow');
    }

    return (
      <Typography variant="body2-secondary" data-fd="opening-time-message">
        {preOrder && <span className={classes.preOrder}>{preOrder} </span>}
        {openTimeMessage}
      </Typography>
    );
  };

  const getStoreUrl = (restaurant: Api.DeliveryRestaurantSummary): string =>
    `/restaurant/${restaurant.VirtualRestaurantId}/delivery/${params.location}`;

  const trackSelectStore = (
    hasConcessionStore: boolean,
    storeName: string,
  ): void => {
    let numberOfRestaurants = 0;
    if (deliveryOption.data.type === 'choice') {
      numberOfRestaurants = deliveryOption.data.of.length;
    }

    const eventData = {
      amp_hasMenuZones: hasConcessionStore,
      amp_label: 'Store Selected',
      amp_orderType: 'Delivery',
      amp_storeSelected: storeName,
      amp_storesPresentedCount: numberOfRestaurants,
    };

    analyticsService.track(eventData);
  };

  const hasRestaurantConcessions = (vrid: number): boolean => {
    if (deliveryOption.data.type !== 'choice') {
      return null;
    }

    const deliveryStore = deliveryOption.data.of.find(
      (store) => store.VirtualRestaurantId === vrid,
    );

    return deliveryStore.HasConcessionStore;
  };

  const getRestaurantItem = (
    restaurant: Api.DeliveryRestaurantSummary,
  ): NavListItemProps => {
    const {
      AcceptPreOrders: acceptPreOrders,
      IsOpen: isOpen,
      OpenTimeMessage: openTimeMessage,
      PhysicalRestaurantAddress: physicalRestaurantAddress,
      VirtualRestaurantId: virtualRestaurantId,
      Name: name,
    } = restaurant;

    const restaurantName = renderRestaurantName(acceptPreOrders, isOpen, name);
    const restaurantAddress = renderAddress(physicalRestaurantAddress);
    const closedNotice = renderClosedNotice(
      acceptPreOrders,
      isOpen,
      openTimeMessage,
    );
    const hasConcessionStore = hasRestaurantConcessions(virtualRestaurantId);

    const isStoreClosed = !isOpen && !acceptPreOrders;
    const closedStatusStyles = isStoreClosed ? classes.disabled : null;
    const itemVariant = isStoreClosed
      ? 'noEndIcon'
      : ('default' as NavListVariantType);

    const restaurantDetails = {
      elements: [
        <div
          className={closedStatusStyles}
          key={`store-${restaurant.PhysicalRestaurantId}`}
        >
          {restaurantName}
          {restaurantAddress}
          {closedNotice}
        </div>,
      ],
      linkCallback: () => trackSelectStore(hasConcessionStore, restaurant.Name),
      linkId: getStoreUrl(restaurant),
      linkPath: getStoreUrl(restaurant),
      variant: itemVariant,
    };

    return restaurantDetails;
  };

  const sortedRestaurants = _sortBy(deliveryOption.data.of, (restaurant) =>
    isRestaurantClosedWithNoPreOrders(
      restaurant.AcceptPreOrders,
      restaurant.IsOpen,
    ),
  );

  const restaurantItems = sortedRestaurants
    .map((restaurant) => getRestaurantItem(restaurant))
    .filter((restaurant) => restaurant !== null);

  return (
    <Page pageName="store-delivery-list">
      <PageHeader showProfileMenu title={translate('Order_delivery_from')}>
        <BackLink
          label={translate('Back')}
          linkPath="/delivery"
          onClick={trackBackSelected}
        />
      </PageHeader>

      <article className="fd-content__main">
        {isReorderEnabled && showReorder && (
          <div className={classes.reorder}>
            <Reorder
              deliveryLocationId={params.location}
              isLoading={isOrderHistoryLoading}
              languageCode={languageCode}
              listTitle={
                translate('ReorderCard_RecentDeliveryOrders') as string
              }
              orders={reorderList}
              restaurants={sortedRestaurants}
              setBasket={actions.setBasket}
              setReorderOrderId={actions.setReorderOrderId}
              source="Store list"
              translate={translate}
            />
          </div>
        )}
        <NavList items={restaurantItems} />
      </article>
    </Page>
  );
}
